import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "../i18n/config";

/**
 * Returns information about the language prefix in the current URL.
 * - lang:  the language code if the URL starts with /:lang/, else current i18n lang
 * - hasPrefix: whether the URL actually has a /:lang/ prefix
 * - rest: pathname without the prefix (always starts with "/")
 */
export const useLangPrefix = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  const segs = pathname.split("/").filter(Boolean);
  const maybeLang = segs[0];

  if (maybeLang && SUPPORTED_LANGS.includes(maybeLang)) {
    const rest = "/" + segs.slice(1).join("/");
    return { lang: maybeLang, hasPrefix: true, rest: rest === "/" ? "/" : rest };
  }

  return { lang: i18n.language || DEFAULT_LANG, hasPrefix: false, rest: pathname || "/" };
};

/**
 * Build a path with the given language prefix.
 * - "/foo" + "en" -> "/en/foo"
 * - "/" + "en" -> "/en"
 */
export const withLangPrefix = (path, lang) => {
  if (!path || path === "/") return `/${lang}`;
  const clean = path.startsWith("/") ? path : `/${path}`;
  // strip an existing lang prefix if any
  const segs = clean.split("/").filter(Boolean);
  if (segs[0] && SUPPORTED_LANGS.includes(segs[0])) {
    return `/${lang}/${segs.slice(1).join("/")}`.replace(/\/+$/, "") || `/${lang}`;
  }
  return `/${lang}${clean}`;
};

/**
 * Replace the language prefix in a pathname with `nextLang`.
 * If no prefix exists, prepend one.
 */
export const swapLangPrefix = (pathname, nextLang) => {
  return withLangPrefix(pathname, nextLang);
};
